import { useQuery } from "@tanstack/react-query";
import { format, type Locale } from "date-fns";
import { Eye, Loader2, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/components/language-provider";
import { getApiUrl } from "@/lib/config";

/** One opening or download of the document's file (GET /api/documents/{id}/access-log). */
interface AccessRow {
  at: string;
  fileKind: "original" | "signed" | "audit" | string;
  via: string;
  signed: boolean;
  who?: string | null;
  email?: string | null;
  ip?: string | null;
  userAgent?: string | null;
}

/** Short browser/app name from the user agent (the full string stays in the tooltip). */
function client(ua?: string | null): string | null {
  if (!ua) return null;
  if (/WhatsApp|facebookexternalhit/i.test(ua)) return "WhatsApp";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";
  if (/curl|python|okhttp|axios|node/i.test(ua)) return ua.split(/[/ ]/)[0];
  return null;
}

/** "Quem abriu o arquivo": shown in the document history so the company sees every reading of its file. */
export function DocumentAccessLog({ documentId, enabled, dateLocale }: { documentId: string; enabled: boolean; dateLocale?: { locale?: Locale } }) {
  const { t } = useLanguage();
  const { data, isLoading, isError } = useQuery<AccessRow[]>({
    queryKey: ["/api/documents", documentId, "access-log"],
    enabled,
    queryFn: async () => {
      const res = await fetch(getApiUrl(`/api/documents/${documentId}/access-log`), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("access-log");
      return res.json();
    },
  });

  // Unknown values fall back to the raw code (t returns the key when it has no text).
  const via = (v: string) => { const k = `access.via.${v}`; const s = t(k); return s === k ? v : s; };
  const kind = (k: string) => { const key = `access.kind.${k}`; const s = t(key); return s === key ? k : s; };

  return (
    <div className="space-y-3" data-testid="document-access-log">
      <h4 className="font-medium flex items-center gap-2">
        <Eye className="h-4 w-4" /> {t("access.title")}
      </h4>
      <p className="text-xs text-muted-foreground flex items-start gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
        <span>{t("access.subtitle")}</span>
      </p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">{t("access.error")}</p>
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("access.empty")}</p>
      ) : (
        <div className="space-y-2">
          {data.map((a, i) => (
            <div key={`${a.at}-${i}`} className="flex gap-3 text-sm">
              <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${a.signed ? "bg-primary" : "bg-amber-500"}`} />
              <div className="flex-1 min-w-0">
                <p className="font-medium break-words">
                  {a.who || (a.signed ? via(a.via) : t("access.unknown"))}
                  {a.email && <span className="font-normal text-muted-foreground"> ({a.email})</span>}
                </p>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className="text-[11px] font-normal">{via(a.via)}</Badge>
                  <Badge variant="secondary" className="text-[11px] font-normal">{kind(a.fileKind)}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1" title={a.userAgent || undefined}>
                  {format(new Date(a.at), "PP, HH:mm:ss", dateLocale)}
                  {a.ip && ` • IP: ${a.ip}`}
                  {client(a.userAgent) && ` • ${client(a.userAgent)}`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
